import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { Spin } from "antd";

import css from "../SelectAvailableDishesModal/SelectAvailableDishesModal.module.css";
import { dishesActions } from "../../../../store/slices/dishes-slices";
import { SearchInputComponentModal } from "../SearchInputComponentModal/SearchInputComponentModal";
import SelectSingleComponentModal from "../SelectSingleComponentModal/SelectSingleComponentModal";
import { cycleAvailableDishesActions } from "../../../../store/slices/menu-slices";
import { cycleAvailableDishesService } from "../../../../services/menu-services/cycleAvailableDishes.service";
import { useTranslation } from "react-i18next";

const SelectCycleAvailableDishesModal = ({ cycleDay, setIsOpen }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const methods = useForm();
    const { setValue, reset, control } = methods;

    const { dishesArray, trigger, dishesFiltered, searchActive, status: dishesStatus } = useSelector(
        (store) => store.dishes
    );
    const { dishesArray: cycleAvailableDishes } = useSelector(
        (store) => store.cycleAvailableDishes
    );

    const [selectedDishes, setSelectedDishes] = useState([]);
    const [saving, setSaving] = useState(false);
    const isLoadingDishes = dishesStatus === "loading" || dishesStatus === null;

    useEffect(() => {
        dispatch(dishesActions.clearSearch());
        return () => { dispatch(dishesActions.clearSearch()); };
    }, [dispatch]);

    useEffect(() => {
        dispatch(dishesActions.getAllDishes());
    }, [trigger, dispatch]);

    const dishesToRender = searchActive ? dishesFiltered : dishesArray;

    useEffect(() => {
        const defaultValues = {};
        const initialSelected = [];

        dishesArray.forEach((d) => {
            const isSelected = cycleAvailableDishes.some((item) => item.dish?.id === d.id || item.dishId === d.id);
            defaultValues[d.id] = isSelected;
            initialSelected.push({ id: d.id, checked: isSelected });
        });

        reset(defaultValues);
        setSelectedDishes(initialSelected);
    }, [dishesArray, cycleAvailableDishes, reset]);

    const watchedValues = useWatch({ control });
    const allSelected = dishesToRender.every((d) => watchedValues?.[d.id]);

    useEffect(() => {
        if (watchedValues && selectedDishes.length > 0) {
            setSelectedDishes((prev) =>
                prev.map((dish) => ({ ...dish, checked: watchedValues[dish.id] || false }))
            );
        }
    }, [watchedValues]);

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        dishesToRender.forEach((d) => setValue(d.id, checked));
        const visibleIds = dishesToRender.map((d) => d.id);
        setSelectedDishes((prev) =>
            prev.map((dish) => ({
                ...dish,
                checked: visibleIds.includes(dish.id) ? checked : dish.checked,
            }))
        );
    };

    const isDishSelected = (dishId) =>
        cycleAvailableDishes.some((item) => item.dish?.id === dishId || item.dishId === dishId);

    const handleSave = async () => {
        try {
            setSaving(true);
            const data = selectedDishes
                .filter((d) => d.checked)
                .map((d) => ({ dishId: d.id }));

            await cycleAvailableDishesService.editByDay(cycleDay, data);
            dispatch(cycleAvailableDishesActions.changeTrigger());
            toast.success(t("Dishes updated successfully"));
            setIsOpen(false);
            reset();
            setSelectedDishes([]);
        } catch (error) {
            console.error(error);
            toast.error(t("Error updating dishes"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={css.overlay}>
            <div className={css.modal}>
                <div className={css.header}>
                    <h2>{t("Select available dishes")} — {t("Day")} {cycleDay}</h2>
                    <button className={css.closeButton} onClick={() => setIsOpen(false)}>
                        &times;
                    </button>
                </div>

                <div className={css.blockInput}>
                    <SearchInputComponentModal />
                </div>

                <FormProvider {...methods}>
                    <form
                        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
                        className={css.form}
                    >
                        <div className={css.table}>
                            <div className={css.tableHeader}>
                                <div className={css.tableHeaderInput}>
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={handleSelectAll}
                                        aria-label="Select all"
                                    />
                                </div>
                                <div className={css.tableHeaderImage}>Image</div>
                                <div className={css.tableHeaderName}>Name</div>
                                <div className={css.tableHeaderWeight}>Weight</div>
                                <div className={css.tableHeaderWeightNetto}>Weight Netto</div>
                                <div className={css.tableHeaderCat}>Category</div>
                            </div>

                            <div className={css.tableBody}>
                                {isLoadingDishes ? (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
                                        <Spin size="large" />
                                    </div>
                                ) : (
                                    dishesToRender.map((obj) => (
                                        <SelectSingleComponentModal
                                            key={obj.id}
                                            dish={obj}
                                            initialChecked={isDishSelected(obj.id)}
                                        />
                                    ))
                                )}
                            </div>
                        </div>

                        <div className={css.actions}>
                            <button type="button" className={css.cancelButton} onClick={() => setIsOpen(false)}>
                                Cancel
                            </button>
                            <button type="submit" className={css.saveButton} disabled={saving}>
                                {saving ? <Spin size="small" /> : "Save"}
                            </button>
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default SelectCycleAvailableDishesModal;
